"use client";

import React, { useState } from "react";
import Image from "next/image";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, CheckCircle2, Play, Sparkles, X, ChevronRight, ShieldAlert, Utensils, Layers } from "lucide-react";
import Link from "next/link";

interface ProductStory {
  id: string;
  name: string;
  tagline: string;
  badge: string;
  iconSrc: string;
  mediaType: "video" | "image";
  mediaSrc: string;
  problem: string;
  system: string;
  experience: string;
  result: string;
  feedback: {
    quote: string;
    author: string;
    role: string;
  };
  demoUrl?: string;
}

const PRODUCTS: ProductStory[] = [
  {
    id: "dineengage",
    name: "DineEngage™",
    tagline: "QR → WhatsApp Guest Loyalty & Single-Use Voucher Engine",
    badge: "RESTAURANT & HOSPITALITY SAAS",
    iconSrc: "/images/storytelling/products/dineengage-icon.png",
    mediaType: "video",
    mediaSrc: "/videos/restaurant_demo.mp4",
    problem:
      "70% of restaurant guests never return. Traditional paper comment cards are ignored, and food aggregator apps charge up to 30% commissions while hiding guest contact information.",
    system:
      "Guests scan a dynamic table QR code to opt-in via WhatsApp. An atomic, single-use voucher lands on their phone instantly. Floor staff scan and burn the voucher at checkout. Opted-in diners are entered into the owner's CRM for automated anniversary and weekend promotions.",
    experience:
      "Zero app downloads required. Everything operates naturally within WhatsApp. Guests receive personalized discounts, while managers track table revenue and redemption metrics in real time.",
    result:
      "+38% repeat guest visits within 60 days, 1,200+ verified customer WhatsApp profiles captured per branch each month, and zero commission fees paid to aggregators.",
    feedback: {
      quote:
        "DineEngage transformed our Friday night footfall. Diners love receiving instant vouchers on WhatsApp, and our staff burns them in 3 seconds at the counter.",
      author: "Vikram Malhotra",
      role: "Managing Director, Plate & Pour Hospitality",
    },
    demoUrl: "/demos/restaurant",
  },
  {
    id: "pharmaengage",
    name: "PharmaEngage™",
    tagline: "Pharmacy Compliance, Batch Inventory & Prescription Refill Suite",
    badge: "HEALTHCARE & PHARMACEUTICAL PLATFORM",
    iconSrc: "/images/storytelling/products/pharmaengage-logo.png",
    mediaType: "image",
    mediaSrc: "/images/storytelling/products/pharmaengage-hero.png",
    problem:
      "Independent pharmacies lose up to 12% of inventory value to expired medication batches, while chronic patients frequently forget scheduled prescription refills, resulting in health lapses and lost revenue.",
    system:
      "Comprehensive multi-tenant pharmacy operations system. Automates regulatory batch expiration quarantine, alerts floor pharmacists before drug lots expire, and dispatches automated WhatsApp refill reminders 5 days before a patient's supply concludes.",
    experience:
      "Pharmacists manage dispensing with barcode scanning. Patients receive a 1-tap WhatsApp message: 'Reply YES to prepare your monthly refill for pickup.' Inventory balances adjust automatically across all branches.",
    result:
      "-42% inventory expiration losses, +52% repeat prescription refills captured, and 100% compliance during drug regulatory inspections.",
    feedback: {
      quote:
        "We reduced drug expiration write-offs by over 40%. The automated WhatsApp refill alerts have become essential for our chronic care patients.",
      author: "Dr. Ananya Rao",
      role: "Chief Pharmacist, MedCare Retail Network",
    },
    demoUrl: "/demos/dental",
  },
  {
    id: "johnson-crm",
    name: "Johnson CRM",
    tagline: "Flat-Tier Pipeline CRM with Autonomous WhatsApp Intelligence",
    badge: "ENTERPRISE BUSINESS OS",
    iconSrc: "/images/storytelling/products/johnson-crm-icon.png",
    mediaType: "image",
    mediaSrc: "/images/storytelling/products/johnson-crm-demo.webp",
    problem:
      "Conventional CRMs penalize business expansion by charging ₹3,000 to ₹8,000 per user per month. Adding 20 employees creates prohibitive monthly SaaS taxes, while lead data remains locked in proprietary silos.",
    system:
      "Self-contained or managed multi-tenant CRM engine with zero per-seat licensing fees. Features unified WhatsApp Business Cloud API sync, automated AI lead triage, Kanban pipelines, stage approvals, payment tracking, and compliant audit trails.",
    experience:
      "Incoming leads from Google Ads, Meta, and website forms trigger instant sub-30-second WhatsApp greetings. Sales reps manage deals across Kanban boards with direct WhatsApp chat drawers without leaving the dashboard.",
    result:
      "Zero monthly per-user licensing taxes, 100% code and database ownership, 3.2x faster lead response times, and complete data privacy compliance.",
    feedback: {
      quote:
        "Switching from Salesforce to Johnson CRM saved our agency over ₹1.8L every month in licensing alone, while our closers respond to leads 3x faster through WhatsApp.",
      author: "Rohan Varma",
      role: "Head of Growth, Zenith Enterprises",
    },
    demoUrl: "/crm",
  },
];

export const Act3ProductGallery: React.FC = () => {
  const [activeProduct, setActiveProduct] = useState<ProductStory | null>(null);

  return (
    <section id="scene-products" className="relative w-full py-24 px-6 sm:px-8 bg-[#F8F8F6] text-[#111318] border-b border-[#111318]/10">
      <div className="w-full max-w-7xl mx-auto">
        {/* Gallery Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16 pb-8 border-b border-[#111318]/10">
          <div>
            <div className="inline-flex items-center gap-2 text-xs font-mono tracking-widest text-[#2563EB] uppercase font-semibold mb-2">
              <Sparkles className="w-3.5 h-3.5" />
              <span>PRODUCT SHOWROOM</span>
            </div>
            <h2 className="text-3xl sm:text-5xl font-extrabold text-[#111318] tracking-tight">
              SOFTWARE WE OWN &amp; ENGINEER.
            </h2>
          </div>
          <p className="text-base text-[#5E636B] max-w-md leading-relaxed">
            These are not conceptual mockups. These are self-contained, multi-tenant digital products engineered and maintained by Johnson Softwares. Click any display for the complete architectural breakdown.
          </p>
        </div>

        {/* Clean Architectural Product Showroom Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {PRODUCTS.map((prod) => (
            <div
              key={prod.id}
              onClick={() => setActiveProduct(prod)}
              className="group cursor-pointer rounded-2xl bg-white border border-[#111318]/10 hover:border-[#2563EB]/40 transition-all duration-300 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_16px_40px_rgb(0,0,0,0.08)] flex flex-col justify-between overflow-hidden"
            >
              {/* Product Visual Plaque */}
              <div className="p-7 pb-0">
                <div className="flex items-center justify-between gap-4 mb-4">
                  <div className="w-14 h-14 rounded-2xl bg-[#F8F8F6] border border-[#111318]/5 p-2.5 flex items-center justify-center group-hover:scale-105 transition-transform">
                    <Image
                      src={prod.iconSrc}
                      alt={prod.name}
                      width={48}
                      height={48}
                      className="object-contain"
                    />
                  </div>
                  <span className="text-[10px] font-mono tracking-wider px-2.5 py-1 rounded-full bg-[#F8F8F6] border border-[#111318]/10 text-[#5E636B] font-semibold">
                    {prod.badge}
                  </span>
                </div>

                <h3 className="text-2xl font-bold text-[#111318] group-hover:text-[#2563EB] transition-colors mb-2">
                  {prod.name}
                </h3>
                <p className="text-xs text-[#5E636B] line-clamp-2 mb-4 leading-relaxed">
                  {prod.tagline}
                </p>
              </div>

              {/* Media Preview Box */}
              <div className="relative h-48 mx-7 rounded-xl overflow-hidden bg-[#F8F8F6] border border-[#111318]/5">
                {prod.mediaType === "video" ? (
                  <div className="relative w-full h-full">
                    <video
                      src={prod.mediaSrc}
                      autoPlay
                      loop
                      muted
                      playsInline
                      className="w-full h-full object-cover filter brightness-[0.95]"
                    />
                    <div className="absolute inset-0 bg-black/10 flex items-center justify-center">
                      <div className="w-10 h-10 rounded-full bg-[#2563EB] text-white flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                        <Play className="w-4 h-4 ml-0.5" />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="relative w-full h-full">
                    <Image
                      src={prod.mediaSrc}
                      alt={prod.name}
                      fill
                      sizes="(max-width: 1024px) 100vw, 33vw"
                      className="object-cover group-hover:scale-105 transition-all duration-500"
                    />
                  </div>
                )}
              </div>

              {/* Bottom Result Snippet */}
              <div className="p-7 pt-4 mt-4 border-t border-[#111318]/10 flex items-center justify-between text-xs">
                <span className="text-emerald-700 font-mono font-semibold flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Production Live
                </span>
                <span className="text-[#5E636B] group-hover:text-[#111318] flex items-center gap-1 font-mono transition-colors font-medium">
                  Inspect Story <ChevronRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* =========================================================================
          INTERACTIVE PRODUCT STORY MODAL (LIGHT EDITORIAL)
      ========================================================================= */}
      <AnimatePresence>
        {activeProduct && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 overflow-y-auto overscroll-contain">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setActiveProduct(null)}
              className="fixed inset-0 bg-[#111318]/50 backdrop-blur-sm"
            />

            <motion.div
              initial={{ opacity: 0, scale: 0.98, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.98, y: 15 }}
              transition={{ duration: 0.25 }}
              className="relative w-full max-w-4xl bg-white border border-[#111318]/10 rounded-3xl shadow-2xl overflow-hidden z-10 my-8 text-[#111318] max-h-[85vh] flex flex-col"
            >
              {/* Drawer Header */}
              <div className="flex items-center justify-between px-8 py-6 border-b border-[#111318]/10 bg-[#F8F8F6] shrink-0">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-white border border-[#111318]/10 p-2 flex items-center justify-center shadow-sm">
                    <Image
                      src={activeProduct.iconSrc}
                      alt={activeProduct.name}
                      width={40}
                      height={40}
                      className="object-contain"
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-xl sm:text-2xl font-bold text-[#111318]">{activeProduct.name}</h3>
                      <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-[#2563EB]/10 text-[#2563EB] font-bold">
                        {activeProduct.badge}
                      </span>
                    </div>
                    <p className="text-xs text-[#5E636B]">{activeProduct.tagline}</p>
                  </div>
                </div>

                <button
                  onClick={() => setActiveProduct(null)}
                  className="p-2 rounded-lg text-[#5E636B] hover:text-[#111318] hover:bg-[#F1F2F3] transition-colors"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Drawer Content */}
              <div className="p-8 space-y-6 overflow-y-auto">
                {/* Media Stage */}
                <div className="relative rounded-2xl overflow-hidden bg-[#F8F8F6] border border-[#111318]/10 aspect-video max-h-72 w-full">
                  {activeProduct.mediaType === "video" ? (
                    <video
                      src={activeProduct.mediaSrc}
                      controls
                      autoPlay
                      loop
                      playsInline
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Image
                      src={activeProduct.mediaSrc}
                      alt={activeProduct.name}
                      fill
                      sizes="100vw"
                      className="object-cover"
                    />
                  )}
                </div>

                {/* The 4-Stage Architectural Breakdown */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-5 rounded-2xl bg-[#F8F8F6] border border-[#111318]/5 space-y-2">
                    <div className="flex items-center gap-2 text-xs font-mono text-rose-600 uppercase font-bold">
                      <ShieldAlert className="w-4 h-4" />
                      <span>The Business Problem</span>
                    </div>
                    <p className="text-xs sm:text-sm text-[#5E636B] leading-relaxed">
                      {activeProduct.problem}
                    </p>
                  </div>

                  <div className="p-5 rounded-2xl bg-[#F8F8F6] border border-[#111318]/5 space-y-2">
                    <div className="flex items-center gap-2 text-xs font-mono text-[#2563EB] uppercase font-bold">
                      <Layers className="w-4 h-4" />
                      <span>The Engineered System</span>
                    </div>
                    <p className="text-xs sm:text-sm text-[#5E636B] leading-relaxed">
                      {activeProduct.system}
                    </p>
                  </div>

                  <div className="p-5 rounded-2xl bg-[#F8F8F6] border border-[#111318]/5 space-y-2">
                    <div className="flex items-center gap-2 text-xs font-mono text-cyan-600 uppercase font-bold">
                      <Utensils className="w-4 h-4" />
                      <span>The User Experience</span>
                    </div>
                    <p className="text-xs sm:text-sm text-[#5E636B] leading-relaxed">
                      {activeProduct.experience}
                    </p>
                  </div>

                  <div className="p-5 rounded-2xl bg-[#F8F8F6] border border-[#111318]/5 space-y-2">
                    <div className="flex items-center gap-2 text-xs font-mono text-emerald-700 uppercase font-bold">
                      <CheckCircle2 className="w-4 h-4" />
                      <span>Documented Results</span>
                    </div>
                    <p className="text-xs sm:text-sm text-[#5E636B] leading-relaxed">
                      {activeProduct.result}
                    </p>
                  </div>
                </div>

                {/* Customer Feedback */}
                <div className="p-6 rounded-2xl bg-white border border-[#111318]/10 text-[#5E636B] text-xs sm:text-sm italic shadow-sm">
                  <p className="mb-2 leading-relaxed">“{activeProduct.feedback.quote}”</p>
                  <div className="not-italic text-xs font-mono text-[#111318]">
                    <strong>{activeProduct.feedback.author}</strong> — {activeProduct.feedback.role}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between px-8 py-5 border-t border-[#111318]/10 bg-[#F8F8F6] shrink-0">
                <button
                  onClick={() => setActiveProduct(null)}
                  className="text-xs font-mono text-[#5E636B] hover:text-[#111318] transition-colors"
                >
                  ← Close Display
                </button>

                <div className="flex items-center gap-3">
                  {activeProduct.demoUrl && (
                    <Link
                      href={activeProduct.demoUrl}
                      className="px-5 py-2.5 rounded-xl bg-white hover:bg-[#F1F2F3] border border-[#111318]/10 text-[#111318] text-xs font-semibold transition-all flex items-center gap-1.5 shadow-sm"
                    >
                      <span>Launch Sandbox</span>
                      <ArrowUpRight className="w-3.5 h-3.5" />
                    </Link>
                  )}
                  <Link
                    href="/contact"
                    className="px-6 py-2.5 rounded-xl bg-[#2563EB] hover:bg-blue-700 text-white text-xs font-semibold transition-all shadow-[0_4px_14px_rgba(37,99,235,0.25)] flex items-center gap-1.5"
                  >
                    <span>Deploy For My Business</span>
                  </Link>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </section>
  );
};
